// src/components/AnalyticsDashboard.js
import React, {useState, useEffect, useRef} from 'react';
import {
    Box, Grid, Paper, Typography, Card, CardContent, CardHeader,
    Divider, CircularProgress, Button, ButtonGroup, Chip, Avatar, Badge,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    List, ListItem, ListItemText, ListItemAvatar,
    Alert, Tabs, Tab, Tooltip, LinearProgress,
    useMediaQuery, IconButton, Collapse, Menu, MenuItem,
    SwipeableDrawer, ListItemButton, ListItemIcon, TextField
} from '@mui/material';
import {
    Warning, Error, Info, TrendingUp, AccessTime, Person,
    CalendarToday, Refresh, MoreVert,
    ExpandMore, ExpandLess, Dashboard, DirectionsRun,
    MonetizationOn, FitnessCenter, PeopleAlt, TrendingUpOutlined,
    FilterList, KeyboardArrowRight
} from '@mui/icons-material';
import {useTheme} from '@mui/material/styles';
import {analyticsApi} from '../api/analyticsApi';
import NewMembersRetention from './NewMembersRetention';
import ContractPlanMetrics from './ContractPlanMetrics';
import RevenueAnalysis from './RevenueAnalysis';
import {Line, Bar, Pie} from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    RadialLinearScale,
    Filler,
    Title,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {getAffiliateById} from "../api/affiliateApi";
import {formatCurrency} from "../utils/currency";

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    RadialLinearScale,
    Filler,
    Title,
    ChartTooltip,
    Legend
);

const FREQUENCY_COLORS = ['#E7E2DC', '#F6D7B0', '#FFB347', '#E8942A', '#C46A1A', '#3A3A3A'];

const getRelativeHeatColor = (count, max) => {
    if (!count || !max) return 'rgba(228, 224, 219, 0.7)';
    const ratio = count / max;
    if (ratio >= 0.85) return '#1B5E20';
    if (ratio >= 0.6) return '#43A047';
    if (ratio >= 0.35) return '#F6C445';
    if (ratio >= 0.15) return '#FFB347';
    return '#F6D7B0';
};

const formatShortDay = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const formatWeekday = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { weekday: 'short' });
};

const activityAreaFill = (context) => {
    const { chart } = context;
    const { ctx, chartArea } = chart;
    if (!chartArea) return 'rgba(255, 179, 71, 0.18)';
    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    gradient.addColorStop(0, 'rgba(255, 179, 71, 0.42)');
    gradient.addColorStop(1, 'rgba(255, 179, 71, 0.02)');
    return gradient;
};

const formatDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return [
        String(date.getDate()).padStart(2, '0'),
        String(date.getMonth() + 1).padStart(2, '0'),
        date.getFullYear()
    ].join('.');
};

const AnalyticsDashboard = ({affiliateId, affiliateEmail}) => {
    // Theme and responsive breakpoints
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [affiliate, setAffiliate] = useState(null);
    // Drawer state for mobile period selector
    const [periodDrawerOpen, setPeriodDrawerOpen] = useState(false);
    const [cardMenuAnchorEl, setCardMenuAnchorEl] = useState(null);
    const [expandedCards, setExpandedCards] = useState({});
    const paymentCurrency = affiliate?.paymentCurrency || "EUR";
    const formatMoney = (value) => formatCurrency(value, paymentCurrency);

    // Helper function to get first and last day of current month
    const getCurrentMonthDates = () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return {
            start: firstDay.toISOString().split('T')[0],
            end: lastDay.toISOString().split('T')[0]
        };
    };

    const currentMonth = getCurrentMonthDates();

    // Date range states for different tabs
    const [startDate, setStartDate] = useState(currentMonth.start);
    const [endDate, setEndDate] = useState(currentMonth.end);
    
    // Keep period only for RevenueAnalysis component (old system)
    const [period, setPeriod] = useState('THIS_MONTH');
    
    // Create dateRange string for API calls
    const dateRange = `${startDate}_${endDate}`;

    const token = localStorage.getItem('token');

    useEffect(() => {
        const loadAffiliateCurrency = async () => {
            if (!affiliateId) {
                setAffiliate(null);
                return;
            }

            try {
                const affiliateData = await getAffiliateById(affiliateId);
                setAffiliate(affiliateData || null);
            } catch (error) {
                console.error('Error loading affiliate currency for analytics:', error);
                setAffiliate(null);
            }
        };

        loadAffiliateCurrency();
    }, [affiliateId]);

    // States for each analytics section
    const [dashboardData, setDashboardData] = useState(null);
    const [activityData, setActivityData] = useState(null);
    const [heatmapData, setHeatmapData] = useState(null);
    const [capacityData, setCapacityData] = useState(null);
    const [capacityAdultVisitsPerWeek, setCapacityAdultVisitsPerWeek] = useState(2.5);
    const [capacityKidsVisitsPerWeek, setCapacityKidsVisitsPerWeek] = useState(1);
    const [capacityAdultVisitsInput, setCapacityAdultVisitsInput] = useState('2.5');
    const [capacityKidsVisitsInput, setCapacityKidsVisitsInput] = useState('1');
    const [capacityIncludeOpenGym, setCapacityIncludeOpenGym] = useState(false);
    const [atRiskData, setAtRiskData] = useState(null);
    const [visitFrequencyData, setVisitFrequencyData] = useState(null);
    const [contractsData, setContractsData] = useState(null);
    const [arpuData, setArpuData] = useState(null);
    const [paymentHealthData, setPaymentHealthData] = useState(null);
    const [suspendedContractsData, setSuspendedContractsData] = useState(null);
    const [expiringContractsData, setExpiringContractsData] = useState(null);
    const [classCapacityData, setClassCapacityData] = useState(null);
    const [trainingTypesData, setTrainingTypesData] = useState(null);
    const [trainerComparisonData, setTrainerComparisonData] = useState(null);
    const [churnData, setChurnData] = useState(null);
    const [clientLifecycleData, setClientLifecycleData] = useState(null);
    const [clientAchievementsData, setClientAchievementsData] = useState(null);
    const [dormantClientsData, setDormantClientsData] = useState(null);
    const [growthOpportunitiesData, setGrowthOpportunitiesData] = useState(null);
    const [earlyWarningsData, setEarlyWarningsData] = useState(null);
    const [topMembersData, setTopMembersData] = useState(null);
    const [topMembersCountMode, setTopMembersCountMode] = useState('checkIn');

    // Chart options adjusted for mobile
    const getChartOptions = (isMobileView = false) => {
        return {
            responsive: true,
            maintainAspectRatio: false,
            animation: {
                duration: 1000
            },
            scales: {
                x: {
                    display: !isMobileView, // Hide x-axis on mobile
                    ticks: {
                        maxRotation: isMobileView ? 45 : 0,
                        font: {
                            size: isMobileView ? 10 : 12
                        }
                    }
                },
                y: {
                    ticks: {
                        font: {
                            size: isMobileView ? 10 : 12
                        }
                    }
                }
            },
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        boxWidth: isMobileView ? 10 : 40,
                        padding: isMobileView ? 10 : 20,
                        font: {
                            size: isMobileView ? 10 : 12
                        }
                    }
                },
                tooltip: {
                    titleFont: {
                        size: isMobileView ? 10 : 14
                    },
                    bodyFont: {
                        size: isMobileView ? 10 : 14
                    },
                    displayColors: !isMobileView
                }
            }
        };
    };

    // Use ref to track if charts have been resized
    const chartContainers = useRef({});

    // Helper for card expansion
    const handleCardExpandToggle = (cardId) => {
        setExpandedCards(prev => ({
            ...prev,
            [cardId]: !prev[cardId]
        }));
    };

    // Menu handlers
    const handleCardMenuOpen = (event) => {
        setCardMenuAnchorEl(event.currentTarget);
    };

    const handleCardMenuClose = () => {
        setCardMenuAnchorEl(null);
    };

    // Handle date range change
    const handleDateRangeChange = () => {
        // Reset data for current tab to trigger refetch
        switch (activeTab) {
            case 0:
                setDashboardData(null);
                setTopMembersData(null);
                break;
            case 1:
                setActivityData(null);
                setHeatmapData(null);
                setCapacityData(null);
                setAtRiskData(null);
                setVisitFrequencyData(null);
                break;
            case 2:
                setContractsData(null);
                setArpuData(null);
                setPaymentHealthData(null);
                setSuspendedContractsData(null);
                setExpiringContractsData(null);
                break;
            case 3:
                setClassCapacityData(null);
                setTrainingTypesData(null);
                setTrainerComparisonData(null);
                break;
            case 4:
                setChurnData(null);
                setClientLifecycleData(null);
                setClientAchievementsData(null);
                break;
            case 5:
                setDormantClientsData(null);
                setGrowthOpportunitiesData(null);
                setEarlyWarningsData(null);
                break;
            default:
                break;
        }

        // Close period drawer after selection on mobile
        if (isMobile) {
            setPeriodDrawerOpen(false);
        }
    };

    // Fetch data for active tab
    useEffect(() => {
        const fetchTabData = async () => {
            if (!affiliateId || !token || !startDate || !endDate) return;

            setLoading(true);
            try {
                switch (activeTab) {
                    case 0: // Dashboard overview
                        if (!dashboardData) {
                            const data = await analyticsApi.getDashboardOverview(token, affiliateId, dateRange);
                            setDashboardData(data);
                        }
                        if (!topMembersData) {
                            const data = await analyticsApi.getTopMembersByCheckIns(token, affiliateId, dateRange);
                            setTopMembersData(data);
                        }
                        break;

                    case 1: // Activity & Behavior
                        if (!activityData) {
                            const data = await analyticsApi.getActivityMetrics(token, affiliateId, dateRange);
                            setActivityData(data);
                        }
                        if (!heatmapData) {
                            const data = await analyticsApi.getVisitHeatmap(token, affiliateId, dateRange);
                            setHeatmapData(data);
                        }
                        if (!capacityData) {
                            const data = await analyticsApi.getCapacityAnalysis(
                                token,
                                affiliateId,
                                dateRange,
                                capacityAdultVisitsPerWeek,
                                capacityKidsVisitsPerWeek,
                                capacityIncludeOpenGym
                            );
                            setCapacityData(data);
                        }
                        if (!atRiskData) {
                            const data = await analyticsApi.getAtRiskMembers(token, affiliateId, dateRange);
                            setAtRiskData(data);
                        }
                        if (!visitFrequencyData) {
                            const data = await analyticsApi.getVisitFrequency(token, affiliateId, dateRange);
                            setVisitFrequencyData(data);
                        }
                        break;

                    case 2: // Financial Analysis
                        if (!contractsData) {
                            const data = await analyticsApi.getContractOverview(token, affiliateId, dateRange);
                            setContractsData(data);
                        }
                        if (!arpuData) {
                            const data = await analyticsApi.getArpu(token, affiliateId, dateRange);
                            setArpuData(data);
                        }
                        if (!paymentHealthData) {
                            const data = await analyticsApi.getPaymentHealth(token, affiliateId, dateRange);
                            setPaymentHealthData(data);
                        }
                        if (!suspendedContractsData) {
                            const data = await analyticsApi.getSuspendedContracts(token, affiliateId, dateRange);
                            setSuspendedContractsData(data);
                        }
                        if (!expiringContractsData) {
                            const data = await analyticsApi.getContractExpirations(token, affiliateId, dateRange);
                            setExpiringContractsData(data);
                        }
                        break;

                    case 3: // Training & Service Analysis
                        if (!classCapacityData) {
                            const data = await analyticsApi.getClassCapacity(token, affiliateId, dateRange);
                            setClassCapacityData(data);
                        }
                        if (!trainingTypesData) {
                            const data = await analyticsApi.getTrainingTypePopularity(token, affiliateId, dateRange);
                            setTrainingTypesData(data);
                        }
                        if (!trainerComparisonData) {
                            const data = await analyticsApi.getTrainerComparison(token, affiliateId, dateRange);
                            setTrainerComparisonData(data);
                        }
                        break;

                    case 4: // Client Retention & Engagement
                        if (!churnData) {
                            const data = await analyticsApi.getChurnAnalysis(token, affiliateId, dateRange);
                            setChurnData(data);
                        }
                        if (!clientLifecycleData) {
                            const data = await analyticsApi.getClientLifecycle(token, affiliateId, dateRange);
                            setClientLifecycleData(data);
                        }
                        if (!clientAchievementsData) {
                            const data = await analyticsApi.getClientAchievements(token, affiliateId, dateRange);
                            setClientAchievementsData(data);
                        }
                        break;

                    case 5: // Growth Potential & Early Warnings
                        if (!dormantClientsData) {
                            const data = await analyticsApi.getDormantClients(token, affiliateId, dateRange);
                            setDormantClientsData(data);
                        }
                        if (!growthOpportunitiesData) {
                            const data = await analyticsApi.getGrowthOpportunities(token, affiliateId, dateRange);
                            setGrowthOpportunitiesData(data);
                        }
                        if (!earlyWarningsData) {
                            const data = await analyticsApi.getEarlyWarnings(token, affiliateId, dateRange);
                            setEarlyWarningsData(data);
                        }
                        break;

                    default:
                        break;
                }
                setError(null);
            } catch (err) {
                setError(`Failed to load data for ${getTabLabel(activeTab)}. Please try again.`);
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchTabData();
    }, [
        activeTab, affiliateId, token, startDate, endDate,
        dashboardData, topMembersData,
        activityData, heatmapData, capacityData, capacityAdultVisitsPerWeek, capacityKidsVisitsPerWeek, capacityIncludeOpenGym, atRiskData, visitFrequencyData,
        contractsData, arpuData, paymentHealthData, suspendedContractsData, expiringContractsData,
        classCapacityData, trainingTypesData, trainerComparisonData,
        churnData, clientLifecycleData, clientAchievementsData,
        dormantClientsData, growthOpportunitiesData, earlyWarningsData
    ]);

    const handleRefresh = () => {
        // Reset state for current tab
        switch (activeTab) {
            case 0:
                setDashboardData(null);
                setTopMembersData(null);
                break;
            case 1:
                setActivityData(null);
                setHeatmapData(null);
                setCapacityData(null);
                setAtRiskData(null);
                setVisitFrequencyData(null);
                break;
            case 2:
                setContractsData(null);
                setArpuData(null);
                setPaymentHealthData(null);
                setSuspendedContractsData(null);
                setExpiringContractsData(null);
                break;
            case 3:
                setClassCapacityData(null);
                setTrainingTypesData(null);
                setTrainerComparisonData(null);
                break;
            case 4:
                setChurnData(null);
                setClientLifecycleData(null);
                setClientAchievementsData(null);
                break;
            case 5:
                setDormantClientsData(null);
                setGrowthOpportunitiesData(null);
                setEarlyWarningsData(null);
                break;
            default:
                break;
        }
    };

    const applyCapacityVisits = (group) => {
        const isKids = group === 'kids';
        const raw = isKids ? capacityKidsVisitsInput : capacityAdultVisitsInput;
        const current = isKids ? capacityKidsVisitsPerWeek : capacityAdultVisitsPerWeek;
        const setInput = isKids ? setCapacityKidsVisitsInput : setCapacityAdultVisitsInput;
        const setValue = isKids ? setCapacityKidsVisitsPerWeek : setCapacityAdultVisitsPerWeek;
        const parsed = parseFloat(String(raw).replace(',', '.'));
        if (!Number.isFinite(parsed) || parsed <= 0) {
            setInput(String(current));
            return;
        }
        const rounded = Math.round(parsed * 10) / 10;
        setInput(String(rounded));
        if (rounded !== current) {
            setValue(rounded);
            setCapacityData(null);
        }
    };

    const renderCapacityVisitsField = (group) => (
        <TextField
            size="small"
            type="number"
            label="Visits / week"
            value={group === 'kids' ? capacityKidsVisitsInput : capacityAdultVisitsInput}
            onChange={(e) => (group === 'kids' ? setCapacityKidsVisitsInput : setCapacityAdultVisitsInput)(e.target.value)}
            onBlur={() => applyCapacityVisits(group)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') e.target.blur();
            }}
            inputProps={{min: 0.5, max: 14, step: 0.5}}
            sx={{width: isMobile ? 96 : 120}}
        />
    );

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);

        // Reset expanded cards state when changing tabs
        setExpandedCards({});
    };

    const getTabLabel = (index) => {
        const labels = [
            'Dashboard',
            'Activity & Behavior',
            'Financial Analysis',
            'Training Analysis',
            'Client Retention',
            'Growth & Warnings'
        ];
        return labels[index];
    };

    // Short labels for mobile tabs
    const getTabShortLabel = (index) => {
        const labels = [
            'Dashboard',
            'Activity',
            'Finance',
            'Training',
            'Retention',
            'Growth'
        ];
        return labels[index];
    };

    // Icon for each tab
    const getTabIcon = (index) => {
        const icons = [
            <Dashboard fontSize="small"/>,
            <DirectionsRun fontSize="small"/>,
            <MonetizationOn fontSize="small"/>,
            <FitnessCenter fontSize="small"/>,
            <PeopleAlt fontSize="small"/>,
            <TrendingUpOutlined fontSize="small"/>
        ];
        return icons[index];
    };

    const formatEuropeanDate = (dateStr) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    };

    // Format date range for display in dd.mm.yyyy format
    const formatDateRange = () => {
        return `${formatEuropeanDate(startDate)} - ${formatEuropeanDate(endDate)}`;
    };

    // Get period label for displaying in UI
    const getPeriodLabel = (data) => {
        return data && data.periodLabel ? data.periodLabel : '';
    };

    // Helper method to make card header consistent
    const renderCardHeader = (title, subtitle, cardId, actionComponent = null) => {
        return (
            <CardHeader
                title={
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Typography variant={isMobile ? "subtitle1" : "h6"} component="div" sx={{fontWeight: "medium"}}>
                            {title}
                        </Typography>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            {actionComponent}
                            {cardId && (
                                <IconButton
                                    size="small"
                                    onClick={() => handleCardExpandToggle(cardId)}
                                    aria-expanded={expandedCards[cardId]}
                                    aria-label="show more"
                                >
                                    {expandedCards[cardId] ? <ExpandLess/> : <ExpandMore/>}
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                }
                subheader={subtitle ?
                    <Typography variant="body2" color="text.secondary">
                        {subtitle}
                    </Typography> : null
                }
                sx={{
                    p: isMobile ? 1.5 : 2,
                    '& .MuiCardHeader-action': {m: 0}
                }}
            />
        );
    };

    // Capacity analysis helpers
    const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const getUtilizationColor = (percent) => {
        if (percent >= 100) return 'rgba(198, 40, 40, 0.9)';   // Red - over capacity
        if (percent >= 85) return 'rgba(245, 124, 0, 0.85)';   // Orange - near full
        if (percent >= 60) return 'rgba(255, 193, 7, 0.7)';    // Yellow - healthy
        if (percent >= 30) return 'rgba(139, 195, 74, 0.6)';   // Light green - room available
        if (percent > 0) return 'rgba(76, 175, 80, 0.4)';      // Green - lots of room
        return 'rgba(224, 224, 224, 0.3)';                     // Gray - no visits
    };

    const getFillStatus = (percent) => {
        if (percent >= 100) return {color: 'error', label: 'Over capacity at peak'};
        if (percent >= 80) return {color: 'warning', label: 'Approaching capacity'};
        return {color: 'success', label: 'Room to grow'};
    };

    const renderCapacityKpi = (label, value, highlight = false, helper = null) => (
        <Grid item xs={6} sm={4}>
            <Box sx={{
                p: isMobile ? 1 : 1.5,
                borderRadius: 1.5,
                height: '100%',
                bgcolor: highlight ? 'rgba(255, 179, 71, 0.22)' : 'action.hover'
            }}>
                <Typography variant="caption" color="text.secondary" sx={{display: 'block'}}>
                    {label}
                </Typography>
                <Typography variant={isMobile ? 'h6' : 'h5'} sx={{fontWeight: 'bold', lineHeight: 1.2}}>
                    {value}
                </Typography>
                {helper && (
                    <Typography variant="caption" color="text.secondary" sx={{display: 'block'}}>
                        {helper}
                    </Typography>
                )}
            </Box>
        </Grid>
    );

    const renderCapacityGroup = (group, title, emptyMessage, visitsField = null) => {
        if (!group || !group.hasClasses) {
            return (
                <Box sx={{
                    p: isMobile ? 1.5 : 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%'
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1}}>
                        <Typography variant="subtitle1" sx={{fontWeight: 700}}>{title}</Typography>
                        {visitsField}
                    </Box>
                    <Typography variant="body2" color="textSecondary">{emptyMessage}</Typography>
                </Box>
            );
        }

        const fillStatus = getFillStatus(group.fillPercent);
        const slotMap = {};
        (group.slots || []).forEach((slot) => {
            slotMap[`${slot.dayOfWeek}-${slot.hour}`] = slot;
        });
        const activeHours = (group.slots || []).map(s => s.hour);
        const minHour = activeHours.length ? Math.min(...activeHours) : 6;
        const maxHour = activeHours.length ? Math.max(...activeHours) : 22;
        const hours = [];
        for (let h = minHour; h <= maxHour; h++) hours.push(h);

        return (
            <Box sx={{
                p: isMobile ? 1.5 : 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                height: '100%'
            }}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5}}>
                    <Typography variant="subtitle1" sx={{fontWeight: 700}}>{title}</Typography>
                    {visitsField}
                </Box>

                <Grid container spacing={1} sx={{mb: 2}}>
                    {renderCapacityKpi(
                        'Realistic max members',
                        group.realisticMaxMembers,
                        true,
                        'Heatmap-adjusted'
                    )}
                    {renderCapacityKpi(
                        'Theoretical max',
                        group.theoreticalMaxMembers,
                        false,
                        'If demand spread evenly'
                    )}
                    {renderCapacityKpi(
                        'Current (computed)',
                        group.currentComputedMembers,
                        false,
                        `${group.uniqueAttendees} unique attendees`
                    )}
                    {renderCapacityKpi(
                        'Seats / week',
                        Number(group.totalSeatsPerWeek || 0).toFixed(0),
                        false,
                        `${Number(group.classesPerWeek || 0).toFixed(1)} classes / week`
                    )}
                    {renderCapacityKpi(
                        'Visits / week',
                        Number(group.totalVisitsPerWeek || 0).toFixed(0),
                        false,
                        `${group.overallUtilizationPercent}% of seats used`
                    )}
                    {renderCapacityKpi(
                        'Peak slot utilization',
                        `${group.peakUtilizationPercent}%`,
                        false,
                        'Bottleneck'
                    )}
                </Grid>

                <Box sx={{mb: 2}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5}}>
                        <Typography variant="body2" sx={{fontWeight: 'medium'}}>
                            Filled: {group.fillPercent}%
                        </Typography>
                        <Chip size="small" color={fillStatus.color} label={fillStatus.label}/>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(group.fillPercent, 100)}
                        color={fillStatus.color}
                        sx={{height: 10, borderRadius: 5}}
                    />
                    <Typography variant="caption" color="textSecondary">
                        {group.currentComputedMembers} of {group.realisticMaxMembers} realistic members
                        {group.realisticMaxMembers > group.currentComputedMembers
                            ? ` - room for about ${group.realisticMaxMembers - group.currentComputedMembers} more`
                            : ' - the busiest time slot is full'}
                    </Typography>
                </Box>

                <Typography variant="body2" sx={{fontWeight: 'medium', mb: 0.5}}>
                    Seat utilization by day and hour
                </Typography>
                <Box sx={{mb: 2, width: '100%'}}>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: `${isMobile ? 28 : 32}px repeat(7, minmax(0, 1fr))`,
                        gap: isMobile ? 0.3 : 0.5,
                        width: '100%'
                    }}>
                        <Box/>
                        {DAY_SHORT.map((d) => (
                            <Typography key={d} variant="caption" align="center" noWrap sx={{fontWeight: 'medium', minWidth: 0}}>
                                {isMobile ? d[0] : d}
                            </Typography>
                        ))}
                        {hours.map((hour) => (
                            <React.Fragment key={hour}>
                                <Typography variant="caption" sx={{lineHeight: `${isMobile ? 14 : 18}px`}}>
                                    {String(hour).padStart(2, '0')}
                                </Typography>
                                {DAY_SHORT.map((_, dayIdx) => {
                                    const slot = slotMap[`${dayIdx + 1}-${hour}`];
                                    const percent = slot ? slot.utilizationPercent : null;
                                    const tooltip = slot
                                        ? `${DAY_SHORT[dayIdx]} ${hour}:00 - ${slot.visitsPerWeek} visits / ${slot.seatsPerWeek} seats per week (${percent}%)`
                                        : `${DAY_SHORT[dayIdx]} ${hour}:00 - no classes`;
                                    return (
                                        <Tooltip key={dayIdx} title={tooltip} arrow>
                                            <Box sx={{
                                                height: isMobile ? 14 : 18,
                                                minWidth: 0,
                                                borderRadius: 0.5,
                                                backgroundColor: slot ? getUtilizationColor(percent) : 'transparent',
                                                border: slot ? '1px solid rgba(0,0,0,0.08)' : '1px dashed rgba(0,0,0,0.08)'
                                            }}/>
                                        </Tooltip>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>

                <Typography variant="body2" sx={{fontWeight: 'medium', mb: 0.5}}>
                    Bottleneck slots
                </Typography>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Slot</TableCell>
                                <TableCell align="center">Seats / wk</TableCell>
                                <TableCell align="center">Visits / wk</TableCell>
                                <TableCell align="center">Used</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(group.bottlenecks || []).map((slot, index) => (
                                <TableRow key={index}>
                                    <TableCell sx={{whiteSpace: 'nowrap'}}>
                                        {DAY_SHORT[slot.dayOfWeek - 1]} {String(slot.hour).padStart(2, '0')}:00
                                    </TableCell>
                                    <TableCell align="center">{slot.seatsPerWeek}</TableCell>
                                    <TableCell align="center">{slot.visitsPerWeek}</TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            size="small"
                                            label={`${slot.utilizationPercent}%`}
                                            color={slot.utilizationPercent >= 100 ? 'error' : slot.utilizationPercent >= 85 ? 'warning' : 'default'}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    const renderMetricRows = (rows) => {
        const max = Math.max(...rows.map((row) => Number(row.value) || 0), 1);
        return rows.map((row, index) => {
            const value = Number(row.value) || 0;
            const scale = row.max || max;
            const width = value > 0 && scale > 0 ? Math.max(4, (Math.min(value, scale) / scale) * 100) : 0;
            return (
                <Box
                    key={row.key || index}
                    sx={{
                        py: 1.1,
                        borderTop: index === 0 ? 'none' : '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.6}}>
                        <Typography variant="body2" noWrap sx={{fontWeight: 600, flex: 1, minWidth: 0}}>
                            {row.label}
                        </Typography>
                        {row.helper && (
                            <Typography variant="caption" color="text.secondary" sx={{flexShrink: 0}}>
                                {row.helper}
                            </Typography>
                        )}
                        <Typography variant="body2" sx={{fontWeight: 700, flexShrink: 0}}>
                            {row.displayValue ?? value}
                        </Typography>
                    </Box>
                    <Box sx={{height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden'}}>
                        <Box sx={{
                            width: `${width}%`,
                            height: '100%',
                            borderRadius: 4,
                            bgcolor: row.color || '#FFB347'
                        }}/>
                    </Box>
                </Box>
            );
        });
    };

    // Mobile-optimized table container
    const renderMobileTable = (columns, data, heightLimit = 300) => {
        return (
            <TableContainer sx={{maxHeight: heightLimit, overflowX: 'auto'}}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.map((column, index) => (
                                <TableCell key={index} align={column.align || 'left'}
                                           sx={{whiteSpace: 'nowrap', px: 1.5}}>
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                                {columns.map((column, cellIndex) => (
                                    <TableCell key={cellIndex} align={column.align || 'left'}
                                               sx={{whiteSpace: 'nowrap', px: 1.5}}>
                                        {column.render ? column.render(row) : row[column.id]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    // Mobile optimized date range selector
    const renderMobileDateSelector = () => {
        return (
            <Box sx={{mb: 2}}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setPeriodDrawerOpen(true)}
                    startIcon={<CalendarToday/>}
                    fullWidth
                    sx={{justifyContent: 'space-between'}}
                >
                    {formatDateRange()}
                    <KeyboardArrowRight/>
                </Button>

                <SwipeableDrawer
                    anchor="bottom"
                    open={periodDrawerOpen}
                    onClose={() => setPeriodDrawerOpen(false)}
                    onOpen={() => setPeriodDrawerOpen(true)}
                    SwipeAreaProps={{height: 20}}
                    PaperProps={{
                        sx: {
                            mb: 10, // Make sure it's above the bottom navbar
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16
                        }
                    }}
                >
                    <Box sx={{p: 2}}>
                        <Typography variant="subtitle1" gutterBottom sx={{fontWeight: 'medium'}}>
                            Select Date Range
                        </Typography>
                        <Divider sx={{mb: 2}}/>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="End Date"
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    inputProps={{
                                        min: startDate
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleDateRangeChange}
                                >
                                    Apply
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </SwipeableDrawer>
            </Box>
        );
    };

    if (!affiliateId) {
        return (
            <Alert severity="warning">
                Please select an affiliate to view analytics.
            </Alert>
        );
    }

    const displayedTopMembers = topMembersData?.dataByMode?.[topMembersCountMode]
        || topMembersData?.data
        || [];
    const topMembersCountField = topMembersCountMode === 'checkIn' ? 'check_in_count' : 'attendance_count';

    return (
        <Box sx={{width: '100%'}}>
            {/* Header + Actions */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: isMobile ? 2 : 3,
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 1 : 0
            }}>
                <Typography
                    variant={isMobile ? "h6" : "h5"}
                    component="h2"
                    fontWeight="bold"
                    align={isMobile ? "center" : "left"}
                    sx={{width: isMobile ? '100%' : 'auto'}}
                >
                    Analytics Dashboard
                </Typography>

                {isMobile ? (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Refresh/>}
                        onClick={handleRefresh}
                        disabled={loading}
                        fullWidth
                    >
                        Refresh Data
                    </Button>
                ) : (
                    <Box sx={{display: 'flex', gap: 2}}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Refresh/>}
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            Refresh Data
                        </Button>
                    </Box>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{mb: 3}}>
                    {error}
                </Alert>
            )}

            {/* Tabs - Mobile optimized */}
            <Paper
                sx={{
                    mb: 2,
                    borderRadius: theme.shape.borderRadius,
                    overflow: 'hidden'
                }}
                elevation={1}
            >
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    scrollButtons={false}
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTabs-flexContainer': {
                            justifyContent: 'space-evenly'
                        },
                        '& .MuiTab-root': {
                            minWidth: isMobile ? 0 : 100,
                            width: isMobile ? '100%' : 'auto',
                            flex: isMobile ? 1 : 'unset',
                            p: isMobile ? 1 : 2,
                        }
                    }}
                >
                    {[0, 1, 2, 3, 4, 5].map(index => (
                        <Tab
                            key={index}
                            label={isMobile ? undefined : getTabShortLabel(index)}
                            icon={getTabIcon(index)}
                            iconPosition={isMobile ? "top" : "start"}
                            sx={{
                                minHeight: isMobile ? 60 : 72,
                                fontSize: isMobile ? '0.7rem' : '0.875rem',

                            }}
                        />
                    ))}
                </Tabs>
            </Paper>

            {/* Date Range Selector - Desktop version */}
            {!isMobile && (
                <Box sx={{mb: 3}}>
                    <Paper sx={{p: 2}}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="End Date"
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    inputProps={{
                                        min: startDate
                                    }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleDateRangeChange}
                                    startIcon={<FilterList/>}
                                >
                                    Apply
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Box>
            )}

            {/* Date Range Selector - Mobile version */}
            {isMobile && renderMobileDateSelector()}

            {loading && (
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4}}>
                    <CircularProgress size={40} sx={{mb: 2}}/>
                    <Typography variant="body1" color="textSecondary">
                        Loading {getTabLabel(activeTab)} data...
                    </Typography>
                </Box>
            )}

            {!loading && (
                <>
                    {/* Dashboard Overview */}
                    {activeTab === 0 && dashboardData && (
                        <Box>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Showing data for: {getPeriodLabel(dashboardData)}
                            </Typography>

                            <Grid container spacing={isMobile ? 1 : 2} sx={{mb: 3}}>
                                {[
                                    {label: 'Active members', value: dashboardData.totalMembers, helper: 'Enrolled in the period'},
                                    {label: 'Visits', value: dashboardData.visitsInPeriod, helper: dashboardData.periodLabel},
                                    {label: 'Active plans', value: dashboardData.activePlans, helper: 'Valid memberships'}
                                ].map((item) => (
                                    <Grid item xs={12} sm={4} key={item.label}>
                                        <Box sx={{p: isMobile ? 1.5 : 2, borderRadius: 2, bgcolor: 'action.hover', height: '100%'}}>
                                            <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                                            <Typography variant={isMobile ? 'h5' : 'h4'} sx={{fontWeight: 700, lineHeight: 1.15}}>
                                                {item.value}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">{item.helper}</Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Alert Summaries - Stacked on mobile */}
                            <Grid container spacing={isMobile ? 2 : 3} sx={{mb: 3}}>
                                {/* At-Risk Members */}
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        {renderCardHeader(
                                            "At-Risk Members",
                                            null,
                                            "atRiskMembers"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["atRiskMembers"] !== false} timeout="auto">
                                            <CardContent
                                                sx={{
                                                    maxHeight: 300,
                                                    overflow: 'auto',
                                                    p: isMobile ? 1 : 2
                                                }}
                                            >
                                                <Typography
                                                    variant={isMobile ? "caption" : "body2"}
                                                    color="textSecondary"
                                                    sx={{display: 'block', mb: 1.5}}
                                                >
                                                    Compares the previous 30 days with the latest 30 days. Members
                                                    with at least 2 previous visits and a decrease of 50% or more are
                                                    considered at risk.
                                                </Typography>
                                                {dashboardData.atRiskMembers.length > 0 ? (
                                                    dashboardData.atRiskMembers.map((member, index) => (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1.5,
                                                                py: 1.1,
                                                                borderTop: index === 0 ? 'none' : '1px solid',
                                                                borderColor: 'divider'
                                                            }}
                                                        >
                                                            <Box sx={{flex: 1, minWidth: 0}}>
                                                                <Typography variant="body2" noWrap sx={{fontWeight: 600}}>
                                                                    {member.fullName}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Previous 30 days {member.previous_visits} · latest 30 days {member.visits}
                                                                </Typography>
                                                            </Box>
                                                            <Chip label="At risk" color="error" size="small"/>
                                                        </Box>
                                                    ))
                                                ) : (
                                                    <Typography variant="body2" color="textSecondary"
                                                                sx={{textAlign: 'center', py: 3}}>
                                                        No at-risk members detected
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>

                                {/* Expiring Contracts */}
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        {renderCardHeader(
                                            "Expiring Contracts",
                                            null,
                                            "expiringContracts"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["expiringContracts"] !== false} timeout="auto">
                                            <CardContent
                                                sx={{
                                                    maxHeight: 300,
                                                    overflow: 'auto',
                                                    p: isMobile ? 1 : 2
                                                }}
                                            >
                                                {dashboardData.expiringContracts.length > 0 ? (
                                                    dashboardData.expiringContracts.map((contract, index) => {
                                                        const daysUntil = Math.ceil((new Date(contract.validUntil) - new Date()) / (1000 * 60 * 60 * 24));
                                                        return (
                                                            <Box
                                                                key={index}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1.5,
                                                                    py: 1.1,
                                                                    borderTop: index === 0 ? 'none' : '1px solid',
                                                                    borderColor: 'divider'
                                                                }}
                                                            >
                                                                <Box sx={{flex: 1, minWidth: 0}}>
                                                                    <Typography variant="body2" noWrap sx={{fontWeight: 600}}>
                                                                        {contract.user.fullName}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {formatDate(contract.validUntil)} · {daysUntil} days
                                                                    </Typography>
                                                                </Box>
                                                                <Chip
                                                                    label={daysUntil <= 7 ? 'Urgent' : 'Soon'}
                                                                    color={daysUntil <= 7 ? 'error' : 'warning'}
                                                                    size="small"
                                                                />
                                                            </Box>
                                                        );
                                                    })
                                                ) : (
                                                    <Typography variant="body2" color="textSecondary"
                                                                sx={{textAlign: 'center', py: 3}}>
                                                        No contracts expiring in the next 30 days
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>

                                {/* Top Members */}
                                <Grid item xs={12}>
                                    <Card>
                                        {renderCardHeader(
                                            "Top Members by Check-Ins",
                                            topMembersData ? `Most active members in ${topMembersData.periodLabel}` : "",
                                            "topMembers"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["topMembers"] !== false} timeout="auto">
                                            <CardContent sx={{p: isMobile ? 1.5 : 2}}>
                                                <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 1}}>
                                                    <ButtonGroup size="small" aria-label="top members mode">
                                                        <Button
                                                            variant={topMembersCountMode === 'checkIn' ? 'contained' : 'outlined'}
                                                            onClick={() => setTopMembersCountMode('checkIn')}
                                                        >
                                                            Check-in
                                                        </Button>
                                                        <Button
                                                            variant={topMembersCountMode === 'attendance' ? 'contained' : 'outlined'}
                                                            onClick={() => setTopMembersCountMode('attendance')}
                                                        >
                                                            Registrations
                                                        </Button>
                                                    </ButtonGroup>
                                                </Box>
                                                {topMembersData && displayedTopMembers.length > 0 ? (
                                                    renderMetricRows(displayedTopMembers.map((member, index) => ({
                                                        key: `${topMembersCountMode}-${member.userId}-${index}`,
                                                        label: `${index + 1}. ${member.fullName}`,
                                                        value: member[topMembersCountField] || 0
                                                    })))
                                                ) : (
                                                    <Typography variant="body2" color="textSecondary"
                                                                sx={{textAlign: 'center', py: 4}}>
                                                        No top member data available for this period
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* Activity & Behavior - Mobile Optimized */}
                    {activeTab === 1 && (
                        <Box>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Showing data for: {getPeriodLabel(activityData)}
                            </Typography>

                            {/* Activity Graph - Full Width Always */}
                            <Grid container spacing={isMobile ? 2 : 3} sx={{mb: isMobile ? 2 : 3}}>
                                <Grid item xs={12}>
                                    <Card>
                                        {renderCardHeader(
                                            "Member Activity",
                                            "Daily visits across the selected period",
                                            "memberActivity"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["memberActivity"] !== false} timeout="auto">
                                            <CardContent
                                                ref={el => chartContainers.current['memberActivity'] = el}
                                                sx={{ p: isMobile ? 1.5 : 2 }}
                                            >
                                                {activityData ? (
                                                    activityData.data.length > 0 ? (() => {
                                                        const days = activityData.data;
                                                        const totalVisits = days.reduce((sum, day) => sum + Number(day.count || 0), 0);
                                                        const averageVisits = totalVisits / days.length;
                                                        const peakDay = days.reduce((best, day) => (
                                                            Number(day.count || 0) > Number(best.count || 0) ? day : best
                                                        ), days[0]);
                                                        const summary = [
                                                            { label: 'Total visits', value: totalVisits },
                                                            { label: 'Daily average', value: averageVisits.toFixed(1) },
                                                            {
                                                                label: 'Busiest day',
                                                                value: peakDay.count,
                                                                helper: `${formatWeekday(peakDay.date)} ${formatShortDay(peakDay.date)}`
                                                            }
                                                        ];

                                                        return (
                                                            <>
                                                                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                                                                    {summary.map((item) => (
                                                                        <Grid item xs={4} key={item.label}>
                                                                            <Box sx={{
                                                                                px: isMobile ? 1 : 1.5,
                                                                                py: 1.25,
                                                                                borderRadius: 1.5,
                                                                                bgcolor: 'action.hover',
                                                                                height: '100%'
                                                                            }}>
                                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                                    {item.label}
                                                                                </Typography>
                                                                                <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, lineHeight: 1.15 }}>
                                                                                    {item.value}
                                                                                </Typography>
                                                                                {item.helper && (
                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                        {item.helper}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        </Grid>
                                                                    ))}
                                                                </Grid>
                                                                <Box sx={{ height: isMobile ? 220 : 280 }}>
                                                                    <Line
                                                                        data={{
                                                                            labels: days.map(d => formatShortDay(d.date)),
                                                                            datasets: [
                                                                                {
                                                                                    label: 'Daily visits',
                                                                                    data: days.map(d => d.count),
                                                                                    borderColor: '#E8942A',
                                                                                    backgroundColor: activityAreaFill,
                                                                                    fill: true,
                                                                                    tension: 0.35,
                                                                                    borderWidth: 2.5,
                                                                                    pointRadius: 0,
                                                                                    pointHoverRadius: 5,
                                                                                    pointHoverBackgroundColor: '#E8942A',
                                                                                    pointHoverBorderColor: '#fff',
                                                                                    pointHoverBorderWidth: 2
                                                                                }
                                                                            ]
                                                                        }}
                                                                        options={{
                                                                            responsive: true,
                                                                            maintainAspectRatio: false,
                                                                            interaction: { mode: 'index', intersect: false },
                                                                            plugins: {
                                                                                legend: { display: false },
                                                                                tooltip: {
                                                                                    backgroundColor: '#2B2B2B',
                                                                                    titleColor: '#fff',
                                                                                    bodyColor: '#F6F5F5',
                                                                                    padding: 10,
                                                                                    displayColors: false,
                                                                                    callbacks: {
                                                                                        title: (items) => {
                                                                                            const day = days[items[0].dataIndex];
                                                                                            return `${formatWeekday(day.date)} ${formatDate(day.date)}`;
                                                                                        },
                                                                                        label: (item) => `${item.parsed.y} visits`
                                                                                    }
                                                                                }
                                                                            },
                                                                            scales: {
                                                                                x: {
                                                                                    grid: { display: false },
                                                                                    border: { display: false },
                                                                                    ticks: {
                                                                                        maxRotation: 0,
                                                                                        autoSkip: true,
                                                                                        maxTicksLimit: isMobile ? 5 : 8,
                                                                                        color: '#6B6B6B',
                                                                                        font: { size: 11 }
                                                                                    }
                                                                                },
                                                                                y: {
                                                                                    beginAtZero: true,
                                                                                    border: { display: false },
                                                                                    grid: { color: 'rgba(0,0,0,0.06)' },
                                                                                    ticks: {
                                                                                        precision: 0,
                                                                                        color: '#6B6B6B',
                                                                                        font: { size: 11 }
                                                                                    }
                                                                                }
                                                                            }
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </>
                                                        );
                                                    })() : (
                                                        <Typography variant="body2" color="textSecondary" sx={{ py: 3, textAlign: 'center' }}>
                                                            No visits in the selected period.
                                                        </Typography>
                                                    )
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: 200
                                                    }}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Adult Weekly Attendance */}
                            <Grid container spacing={isMobile ? 2 : 3} sx={{mb: isMobile ? 2 : 3}}>
                                <Grid item xs={12}>
                                    <Card>
                                        {renderCardHeader(
                                            "Adult Weekly Attendance",
                                            "Average visits per adult per full week",
                                            "adultWeeklyAttendance"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["adultWeeklyAttendance"] !== false} timeout="auto">
                                            <CardContent sx={{p: isMobile ? 1.5 : 2}}>
                                                {activityData?.adultWeeklyAttendance ? (
                                                    activityData.adultWeeklyAttendance.fullWeeks > 0 ? (
                                                        <>
                                                            <Typography variant="body2" color="text.secondary" sx={{mb: 1.5}}>
                                                                Family members are excluded. Only complete Monday–Sunday weeks are shown.
                                                            </Typography>
                                                            {activityData.adultWeeklyAttendance.weeks.map((week, index) => {
                                                                const average = Number(week.averageVisitsPerAdult || 0);
                                                                const maxAverage = Math.max(
                                                                    ...activityData.adultWeeklyAttendance.weeks.map(item => Number(item.averageVisitsPerAdult || 0)),
                                                                    1
                                                                );
                                                                return (
                                                                    <Box
                                                                        key={index}
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 2,
                                                                            py: 1.25,
                                                                            borderTop: index === 0 ? 'none' : '1px solid',
                                                                            borderColor: 'divider'
                                                                        }}
                                                                    >
                                                                        <Box sx={{minWidth: isMobile ? 108 : 150}}>
                                                                            <Typography variant="body2" sx={{fontWeight: 600}}>
                                                                                {formatShortDay(week.weekStart)} – {formatShortDay(week.weekEnd)}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {week.adultAttendees} adults · {week.totalVisits} visits
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box sx={{flex: 1, height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden'}}>
                                                                            <Box sx={{
                                                                                width: `${average > 0 ? Math.max(4, (average / maxAverage) * 100) : 0}%`,
                                                                                height: '100%',
                                                                                borderRadius: 4,
                                                                                bgcolor: '#FFB347'
                                                                            }}/>
                                                                        </Box>
                                                                        <Typography variant={isMobile ? 'h6' : 'h5'} sx={{fontWeight: 700, minWidth: 42, textAlign: 'right'}}>
                                                                            {average.toFixed(1)}
                                                                        </Typography>
                                                                    </Box>
                                                                );
                                                            })}
                                                        </>
                                                    ) : (
                                                        <Typography variant="body2" color="textSecondary">
                                                            Selected period does not contain a complete Monday-Sunday week.
                                                        </Typography>
                                                    )
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: 120
                                                    }}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Capacity Analysis */}
                            <Grid container spacing={isMobile ? 2 : 3} sx={{mb: isMobile ? 2 : 3}}>
                                <Grid item xs={12}>
                                    <Card>
                                        {renderCardHeader(
                                            "Capacity Analysis",
                                            `Adults ${capacityAdultVisitsPerWeek} and kids ${capacityKidsVisitsPerWeek} visits per member per week`,
                                            "capacityAnalysis"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["capacityAnalysis"] !== false} timeout="auto">
                                            <CardContent sx={{p: isMobile ? 1.5 : 2}}>
                                                {capacityData ? (
                                                    <>
                                                        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                                            Based on {capacityData.weeks} weeks ({capacityData.periodLabel}). Each group uses its own visits-per-week assumption. The busiest regular time slot sets the realistic member limit. Kids classes are counted separately. Open Gym is {capacityIncludeOpenGym ? 'included in' : 'excluded from'} the adult calculation.
                                                        </Typography>
                                                        <Grid container spacing={isMobile ? 2 : 3}>
                                                            <Grid item xs={12} md={6}>
                                                                {renderCapacityGroup(
                                                                    capacityData.adults,
                                                                    'Adults',
                                                                    'No adult classes in the selected period.',
                                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end'}}>
                                                                        <Button
                                                                            size="small"
                                                                            variant={capacityIncludeOpenGym ? 'contained' : 'outlined'}
                                                                            onClick={() => {
                                                                                setCapacityIncludeOpenGym((included) => !included);
                                                                                setCapacityData(null);
                                                                            }}
                                                                            sx={{textTransform: 'none', whiteSpace: 'nowrap'}}
                                                                        >
                                                                            Include Open Gym
                                                                        </Button>
                                                                        {renderCapacityVisitsField('adults')}
                                                                    </Box>
                                                                )}
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                {renderCapacityGroup(
                                                                    capacityData.kids,
                                                                    'Kids',
                                                                    'No Kids classes in the selected period.',
                                                                    renderCapacityVisitsField('kids')
                                                                )}
                                                            </Grid>
                                                        </Grid>
                                                    </>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: 160
                                                    }}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Heatmap & Visit Frequency */}
                            <Grid container spacing={isMobile ? 2 : 3} sx={{mb: isMobile ? 2 : 3}}>
                                <Grid item xs={12} md={7}>
                                    <Card sx={{height: '100%'}}>
                                        {renderCardHeader(
                                            "Visit Heatmap",
                                            "Class registrations by day and hour, not check-ins",
                                            "visitHeatmap"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["visitHeatmap"] !== false} timeout="auto">
                                            <CardContent sx={{p: isMobile ? 1.5 : 2}}>
                                                {heatmapData ? (() => {
                                                    const hours = [];
                                                    for (let hour = 6; hour <= 22; hour += 1) hours.push(hour);
                                                    const maxCount = Math.max(...heatmapData.data.map(item => Number(item.count || 0)), 1);
                                                    const slotLookup = {};
                                                    heatmapData.data.forEach((item) => {
                                                        slotLookup[`${item.dayOfWeek}-${item.hour}`] = Number(item.count || 0);
                                                    });
                                                    return (
                                                        <>
                                                            <Box sx={{
                                                                display: 'grid',
                                                                gridTemplateColumns: `${isMobile ? 28 : 36}px repeat(7, minmax(0, 1fr))`,
                                                                gap: 0.4
                                                            }}>
                                                                <Box/>
                                                                {DAY_SHORT.map((day) => (
                                                                    <Typography key={day} variant="caption" align="center" noWrap sx={{fontWeight: 600}}>
                                                                        {isMobile ? day[0] : day}
                                                                    </Typography>
                                                                ))}
                                                                {hours.map((hour) => (
                                                                    <React.Fragment key={hour}>
                                                                        <Typography variant="caption" color="text.secondary" sx={{lineHeight: '18px'}}>
                                                                            {String(hour).padStart(2, '0')}
                                                                        </Typography>
                                                                        {DAY_SHORT.map((day, dayIndex) => {
                                                                            const count = slotLookup[`${dayIndex + 1}-${hour}`] || 0;
                                                                            return (
                                                                                <Tooltip key={day} title={`${day} ${hour}:00 · ${count} visits`} arrow>
                                                                                    <Box sx={{
                                                                                        height: 18,
                                                                                        borderRadius: 0.5,
                                                                                        bgcolor: getRelativeHeatColor(count, maxCount)
                                                                                    }}/>
                                                                                </Tooltip>
                                                                            );
                                                                        })}
                                                                    </React.Fragment>
                                                                ))}
                                                            </Box>
                                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 1.5}}>
                                                                <Typography variant="caption" color="text.secondary">Quieter</Typography>
                                                                {['#F6D7B0', '#FFB347', '#F6C445', '#43A047', '#1B5E20'].map((color) => (
                                                                    <Box key={color} sx={{width: 18, height: 8, borderRadius: 0.5, bgcolor: color}}/>
                                                                ))}
                                                                <Typography variant="caption" color="text.secondary">Busier</Typography>
                                                            </Box>
                                                        </>
                                                    );
                                                })() : (
                                                    <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={5}>
                                    <Card sx={{height: '100%'}}>
                                        {renderCardHeader(
                                            "Visit Frequency",
                                            "Members with at least one class registration, averaged per week",
                                            "visitFrequency"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["visitFrequency"] !== false} timeout="auto">
                                            <CardContent sx={{p: isMobile ? 1.5 : 2}}>
                                                {visitFrequencyData ? (() => {
                                                    const rows = visitFrequencyData.data || [];
                                                    const totalMembers = rows.reduce((sum, row) => sum + Number(row.member_count || 0), 0);
                                                    return (
                                                        <Box sx={{display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', gap: 2}}>
                                                            <Box sx={{width: isMobile ? 180 : 200, height: isMobile ? 180 : 200, flexShrink: 0}}>
                                                                <Pie
                                                                    data={{
                                                                        labels: rows.map(row => row.frequency_range),
                                                                        datasets: [{
                                                                            data: rows.map(row => Number(row.member_count || 0)),
                                                                            backgroundColor: rows.map((_, index) => FREQUENCY_COLORS[index % FREQUENCY_COLORS.length]),
                                                                            borderWidth: 2,
                                                                            borderColor: '#fff'
                                                                        }]
                                                                    }}
                                                                    options={{
                                                                        responsive: true,
                                                                        maintainAspectRatio: false,
                                                                        cutout: '68%',
                                                                        plugins: {
                                                                            legend: {display: false},
                                                                            tooltip: {
                                                                                backgroundColor: '#2B2B2B',
                                                                                displayColors: false,
                                                                                callbacks: {
                                                                                    label: (item) => `${item.parsed} members`
                                                                                }
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Box sx={{flex: 1, width: '100%'}}>
                                                                {rows.map((row, index) => {
                                                                    const count = Number(row.member_count || 0);
                                                                    const share = totalMembers > 0 ? Math.round((count / totalMembers) * 100) : 0;
                                                                    return (
                                                                        <Box key={row.frequency_range} sx={{display: 'flex', alignItems: 'center', gap: 1, py: 0.6}}>
                                                                            <Box sx={{
                                                                                width: 10,
                                                                                height: 10,
                                                                                borderRadius: '50%',
                                                                                bgcolor: FREQUENCY_COLORS[index % FREQUENCY_COLORS.length],
                                                                                flexShrink: 0
                                                                            }}/>
                                                                            <Typography variant="body2" sx={{flex: 1}}>
                                                                                {row.frequency_range}
                                                                            </Typography>
                                                                            <Typography variant="body2" sx={{fontWeight: 700}}>
                                                                                {count}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary" sx={{width: 36, textAlign: 'right'}}>
                                                                                {share}%
                                                                            </Typography>
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Box>
                                                        </Box>
                                                    );
                                                })() : (
                                                    <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* At-Risk Members */}
                            <Grid container spacing={isMobile ? 2 : 3}>
                                <Grid item xs={12}>
                                    <Card>
                                        {renderCardHeader(
                                            "At-Risk Members",
                                            "Members who have significantly reduced their attendance",
                                            "atRiskMembersDetailed"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["atRiskMembersDetailed"] !== false} timeout="auto">
                                            <CardContent
                                                sx={{
                                                    p: isMobile ? 1 : 2
                                                }}
                                            >
                                                {atRiskData ? (
                                                    <>
                                                        <Typography variant="body2" color="text.secondary" sx={{mb: 1.5}}>
                                                            {atRiskData.comparisonInfo?.currentPeriod} compared with {atRiskData.comparisonInfo?.previousPeriod}
                                                        </Typography>
                                                        {atRiskData.data.length > 0 ? (() => {
                                                            const members = atRiskData.data.map((member) => {
                                                                const previous = Number(member.previous_visits || 0);
                                                                const recent = Number(member.recent_visits || 0);
                                                                const dropPercentage = previous > 0
                                                                    ? Math.round(((previous - recent) / previous) * 100)
                                                                    : 0;
                                                                let riskLevel = 'Medium';
                                                                if (dropPercentage > 80) riskLevel = 'High';
                                                                if (dropPercentage < 40) riskLevel = 'Low';
                                                                return {...member, dropPercentage, riskLevel};
                                                            });
                                                            const riskColor = {
                                                                High: 'error',
                                                                Medium: 'warning',
                                                                Low: 'success'
                                                            };
                                                            return (
                                                                <>
                                                                    <Box sx={{display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap'}}>
                                                                        {['High', 'Medium', 'Low'].map((level) => (
                                                                            <Chip
                                                                                key={level}
                                                                                size="small"
                                                                                color={riskColor[level]}
                                                                                variant="outlined"
                                                                                label={`${members.filter(member => member.riskLevel === level).length} ${level.toLowerCase()}`}
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                    <TableContainer sx={{maxHeight: isMobile ? 320 : 420}}>
                                                                        <Table size="small" stickyHeader>
                                                                            <TableHead>
                                                                                <TableRow>
                                                                                    <TableCell>Member</TableCell>
                                                                                    <TableCell align="center">Recent</TableCell>
                                                                                    <TableCell align="center">Before</TableCell>
                                                                                    <TableCell align="center">Drop</TableCell>
                                                                                    {!isMobile && <TableCell align="center">Last visit</TableCell>}
                                                                                    <TableCell align="center">Risk</TableCell>
                                                                                </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                                {members.map((member, index) => (
                                                                                    <TableRow key={index} hover>
                                                                                        <TableCell>
                                                                                            <Typography variant="body2" sx={{fontWeight: 600}}>
                                                                                                {member.fullName}
                                                                                            </Typography>
                                                                                            {!isMobile && (
                                                                                                <Typography variant="caption" color="text.secondary">
                                                                                                    {member.email}
                                                                                                </Typography>
                                                                                            )}
                                                                                        </TableCell>
                                                                                        <TableCell align="center">{member.recent_visits}</TableCell>
                                                                                        <TableCell align="center">{member.previous_visits}</TableCell>
                                                                                        <TableCell align="center" sx={{fontWeight: 700}}>
                                                                                            {member.dropPercentage}%
                                                                                        </TableCell>
                                                                                        {!isMobile && (
                                                                                            <TableCell align="center">
                                                                                                {formatDate(member.last_visit)}
                                                                                            </TableCell>
                                                                                        )}
                                                                                        <TableCell align="center">
                                                                                            <Chip
                                                                                                label={member.riskLevel}
                                                                                                color={riskColor[member.riskLevel]}
                                                                                                size="small"
                                                                                            />
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                ))}
                                                                            </TableBody>
                                                                        </Table>
                                                                    </TableContainer>
                                                                </>
                                                            );
                                                        })() : (
                                                            <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 3}}>
                                                                No at-risk members detected
                                                            </Typography>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: 200
                                                    }}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* Financial Analysis - Mobile Optimized */}
                    {activeTab === 2 && (
                        <Box>


                            {/* Contracts Overview & ARPU - Stack on Mobile */}
                            <Grid container spacing={isMobile ? 2 : 3} sx={{mb: isMobile ? 2 : 3}}>

                                <Grid item xs={12}>
                                    <RevenueAnalysis
                                        affiliateId={affiliateId}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card>
                                        {renderCardHeader(
                                            "Average Revenue Per User (ARPU)",
                                            arpuData ? `Period: ${arpuData.periodLabel}` : "Monthly trend",
                                            "arpu"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["arpu"] !== false} timeout="auto">
                                            <CardContent
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: isMobile ? 200 : 240,
                                                    p: isMobile ? 1 : 2
                                                }}
                                            >
                                                {arpuData ? (
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography
                                                            variant={isMobile ? "h4" : "h3"}
                                                            sx={{
                                                                mb: 1,
                                                                fontWeight: 'bold',
                                                                color: 'primary.main'
                                                            }}
                                                        >
                                                            {formatMoney(arpuData.data.length > 0
                                                                ? arpuData.data.reduce((sum, d) => sum + d.arpu, 0) / arpuData.data.length
                                                                : 0
                                                            )}
                                                        </Typography>
                                                        <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                                                            Average Revenue Per User
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            Based on {arpuData.data.length} month{arpuData.data.length !== 1 ? 's' : ''} of data
                                                        </Typography>
                                                        {arpuData.data.length > 1 && (
                                                            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                                                Range: {formatMoney(Math.min(...arpuData.data.map(d => d.arpu)))} - {formatMoney(Math.max(...arpuData.data.map(d => d.arpu)))}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: '100%'
                                                    }}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card>
                                        {renderCardHeader(
                                            "Unused Credit",


                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["unusedCredit"] !== false} timeout="auto">
                                            <CardContent
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: isMobile ? 200 : 240,
                                                    p: isMobile ? 1 : 2
                                                }}
                                            >
                                                {arpuData ? (
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography
                                                            variant={isMobile ? "h4" : "h3"}
                                                            sx={{
                                                                mb: 1,
                                                                fontWeight: 'bold',
                                                                color: 'warning.main'
                                                            }}
                                                        >
                                                            {formatMoney(arpuData.unusedCredit || 0)}
                                                        </Typography>
                                                        <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                                                            Total Unused Credit
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            Available customer balance
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: '100%'
                                                    }}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Payment Health */}
                            <Grid container spacing={isMobile ? 2 : 3} sx={{mb: isMobile ? 2 : 3}}>
                                <Grid item xs={12}>
                                    <Card>
                                        {renderCardHeader(
                                            "Payment Health",
                                            paymentHealthData ? `Period: ${paymentHealthData.periodLabel}` : "Payment status and issues",
                                            "paymentHealth"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["paymentHealth"] !== false} timeout="auto">
                                            <CardContent sx={{p: isMobile ? 1 : 2}}>
                                                <Grid container spacing={isMobile ? 2 : 3}>
                                                    <Grid item xs={12} md={4}>
                                                        {paymentHealthData ? (
                                                            <Box
                                                                ref={el => chartContainers.current['paymentHealthPie'] = el}
                                                                sx={{
                                                                    height: isMobile ? 180 : 200,
                                                                    display: 'flex',
                                                                    justifyContent: 'center',
                                                                    alignItems: 'center'
                                                                }}
                                                            >
                                                                <Pie
                                                                    data={{
                                                                        labels: paymentHealthData.paymentHealth.map(p => p.payment_status),
                                                                        datasets: [
                                                                            {
                                                                                data: paymentHealthData.paymentHealth.map(p => p.count),
                                                                                backgroundColor: [
                                                                                    'rgba(76, 175, 80, 0.7)',  // Completed
                                                                                    'rgba(255, 193, 7, 0.7)',  // Pending
                                                                                    'rgba(255, 152, 0, 0.7)',  // Delayed
                                                                                    'rgba(244, 67, 54, 0.7)'   // Failed
                                                                                ]
                                                                            }
                                                                        ]
                                                                    }}
                                                                    options={getChartOptions(isMobile)}
                                                                />
                                                            </Box>
                                                        ) : (
                                                            <Box sx={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                height: 200
                                                            }}>
                                                                <CircularProgress size={30}/>
                                                            </Box>
                                                        )}
                                                    </Grid>

                                                    <Grid item xs={12} md={8}>
                                                        <Typography variant="subtitle1" gutterBottom
                                                                    sx={{fontSize: isMobile ? '0.9rem' : '1rem'}}>
                                                            Failed Payments
                                                        </Typography>
                                                        {paymentHealthData ? (
                                                            paymentHealthData.failedPayments.length > 0 ? (
                                                                <TableContainer sx={{
                                                                    maxHeight: isMobile ? 200 : 300,
                                                                    overflowX: 'auto'
                                                                }}>
                                                                    <Table size="small" stickyHeader>
                                                                        <TableHead>
                                                                            <TableRow>
                                                                                <TableCell>Member</TableCell>
                                                                                <TableCell>Amount</TableCell>
                                                                                {!isMobile &&
                                                                                    <TableCell>Date</TableCell>}
                                                                                <TableCell
                                                                                    align="right">Actions</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {paymentHealthData.failedPayments.map((payment, index) => (
                                                                                <TableRow key={index}>
                                                                                    <TableCell>{payment.user.fullName}</TableCell>
                                                                                    <TableCell>{formatMoney(payment.amount)}</TableCell>
                                                                                    {!isMobile &&
                                                                                        <TableCell>{formatDate(payment.createdAt)}</TableCell>}
                                                                                    <TableCell align="right">
                                                                                        <Button size="small"
                                                                                                variant="outlined">
                                                                                            Contact
                                                                                        </Button>
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            ) : (
                                                                <Typography variant="body2" color="textSecondary"
                                                                            sx={{textAlign: 'center', py: 3}}>
                                                                    No failed payments
                                                                    in {paymentHealthData.periodLabel.toLowerCase()}
                                                                </Typography>
                                                            )
                                                        ) : (
                                                            <Box sx={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                height: isMobile ? 100 : 200
                                                            }}>
                                                                <CircularProgress size={30}/>
                                                            </Box>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Payment Holidays & Expiring Contracts - Stack on Mobile */}
                            <Grid container spacing={isMobile ? 2 : 3}>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        {renderCardHeader(
                                            "Payment Holidays",
                                            suspendedContractsData ? `Period: ${suspendedContractsData.periodLabel}` : "Suspended contracts",
                                            "paymentHolidays"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["paymentHolidays"] !== false} timeout="auto">
                                            <CardContent
                                                sx={{
                                                    height: isMobile ? 'auto' : 280,
                                                    p: isMobile ? 1 : 2
                                                }}
                                            >
                                                {suspendedContractsData ? (
                                                    <Box>
                                                        <Typography
                                                            variant={isMobile ? "body2" : "subtitle2"}
                                                            gutterBottom
                                                            sx={{fontWeight: 'medium'}}
                                                        >
                                                            Payment Holidays by Month
                                                        </Typography>
                                                        <Box
                                                            ref={el => chartContainers.current['monthlyHolidays'] = el}
                                                            sx={{
                                                                mb: 3,
                                                                height: isMobile ? 120 : 150
                                                            }}
                                                        >
                                                            {suspendedContractsData.monthlyHolidays?.some(m => m.count > 0) ? (
                                                                <Bar
                                                                    data={{
                                                                        labels: suspendedContractsData.monthlyHolidays.map(m => m.label || m.month),
                                                                        datasets: [
                                                                            {
                                                                                label: 'Payment Holidays',
                                                                                data: suspendedContractsData.monthlyHolidays.map(m => m.count),
                                                                                backgroundColor: 'rgba(255, 159, 64, 0.7)'
                                                                            }
                                                                        ]
                                                                    }}
                                                                    options={getChartOptions(isMobile)}
                                                                />
                                                            ) : (
                                                                <Typography variant="body2" color="textSecondary"
                                                                            sx={{textAlign: 'center', py: 3}}>
                                                                    No payment holidays in {suspendedContractsData.periodLabel.toLowerCase()}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            gap: 1,
                                                            mb: 3
                                                        }}>
                                                            {suspendedContractsData.monthlyHolidays
                                                                ?.filter(month => month.count > 0)
                                                                .map(month => (
                                                                    <Chip
                                                                        key={month.month}
                                                                        label={`${month.label || month.month}: ${month.count}`}
                                                                        color="warning"
                                                                        variant="outlined"
                                                                        size={isMobile ? "small" : "medium"}
                                                                    />
                                                                ))}
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: '100%'
                                                    }}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card>
                                        {renderCardHeader(
                                            "Expiring Contracts",
                                            expiringContractsData ? `Period: ${expiringContractsData.periodLabel}` : "Upcoming contract expirations",
                                            "expiringContracts"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["expiringContracts"] !== false} timeout="auto">
                                            <CardContent
                                                sx={{
                                                    height: isMobile ? 'auto' : 350,
                                                    p: isMobile ? 1 : 2
                                                }}
                                            >
                                                {expiringContractsData ? (
                                                    <Box>
                                                        <Typography
                                                            variant={isMobile ? "body2" : "subtitle2"}
                                                            gutterBottom
                                                            sx={{fontWeight: 'medium'}}
                                                        >
                                                            Monthly Expirations
                                                        </Typography>
                                                        <Box
                                                            ref={el => chartContainers.current['monthlyExpirations'] = el}
                                                            sx={{
                                                                mb: 3,
                                                                height: isMobile ? 120 : 150
                                                            }}
                                                        >
                                                            <Bar
                                                                data={{
                                                                    labels: expiringContractsData.monthlyExpirations.map(m => m.month),
                                                                    datasets: [
                                                                        {
                                                                            label: 'Contracts',
                                                                            data: expiringContractsData.monthlyExpirations.map(m => m.count),
                                                                            backgroundColor: 'rgba(255, 99, 132, 0.7)'
                                                                        },
                                                                        {
                                                                            label: `Value (${paymentCurrency})`,
                                                                            data: expiringContractsData.monthlyExpirations.map(m => m.value),
                                                                            backgroundColor: 'rgba(54, 162, 235, 0.7)',
                                                                            yAxisID: 'y1'
                                                                        }
                                                                    ]
                                                                }}
                                                                options={{
                                                                    ...getChartOptions(isMobile),
                                                                    scales: {
                                                                        y: {
                                                                            type: 'linear',
                                                                            position: 'left',
                                                                            title: {
                                                                                display: !isMobile,
                                                                                text: 'Contract Count'
                                                                            },
                                                                            ticks: {
                                                                                font: {
                                                                                    size: isMobile ? 10 : 12
                                                                                }
                                                                            }
                                                                        },
                                                                        y1: {
                                                                            type: 'linear',
                                                                            position: 'right',
                                                                            grid: {
                                                                                drawOnChartArea: false
                                                                            },
                                                                            title: {
                                                                                display: !isMobile,
                                                                                text: `Value (${paymentCurrency})`
                                                                            },
                                                                            ticks: {
                                                                                font: {
                                                                                    size: isMobile ? 10 : 12
                                                                                }
                                                                            }
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            mt: 2,
                                                            flexDirection: isMobile ? 'column' : 'row',
                                                            gap: isMobile ? 1 : 0
                                                        }}>
                                                            <Box>
                                                                <Typography
                                                                    variant={isMobile ? "body2" : "subtitle2"}
                                                                    gutterBottom
                                                                    sx={{fontWeight: 'medium'}}
                                                                >
                                                                    Total Expirations
                                                                </Typography>
                                                                <Typography variant={isMobile ? "subtitle1" : "h6"}>
                                                                    {expiringContractsData.totalExpirations}
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography
                                                                    variant={isMobile ? "body2" : "subtitle2"}
                                                                    gutterBottom
                                                                    sx={{fontWeight: 'medium'}}
                                                                >
                                                                    At-Risk Revenue
                                                                </Typography>
                                                                <Typography variant={isMobile ? "subtitle1" : "h6"}
                                                                            color="error">
                                                                    {formatMoney(expiringContractsData.atRiskRevenue)}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: '100%'
                                                    }}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <ContractPlanMetrics
                                    affiliateId={affiliateId}
                                    period={dateRange}
                                />
                            </Grid>


                        </Box>
                    )}

                    {/* Training & Service Analysis */}
                    {activeTab === 3 && (
                        <Box>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Showing data for: {getPeriodLabel(classCapacityData)}
                            </Typography>

                            {/* Class Capacity */}
                            <Grid container spacing={isMobile ? 2 : 3} sx={{mb: isMobile ? 2 : 3}}>
                                <Grid item xs={12}>
                                    <Card>
                                        {renderCardHeader(
                                            "Class Capacity",
                                            "Utilization rates by training type",
                                            "classCapacity"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["classCapacity"] !== false} timeout="auto">
                                            <CardContent sx={{p: isMobile ? 1.5 : 2}}>
                                                {classCapacityData ? (
                                                    <>
                                                        <Box sx={{p: 1.5, mb: 1.5, borderRadius: 1.5, bgcolor: 'action.hover'}}>
                                                            <Typography variant="caption" color="text.secondary">Average utilization</Typography>
                                                            <Typography variant="h5" sx={{fontWeight: 700, lineHeight: 1.15}}>
                                                                {classCapacityData.averageUtilization}%
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {classCapacityData.periodLabel}
                                                            </Typography>
                                                        </Box>
                                                        {renderMetricRows(
                                                            Object.entries(classCapacityData.byTrainingType || {})
                                                                .sort((a, b) => b[1].utilization - a[1].utilization)
                                                                .map(([type, data]) => ({
                                                                    key: type,
                                                                    label: type,
                                                                    value: data.utilization,
                                                                    max: 100,
                                                                    displayValue: `${data.utilization}%`,
                                                                    helper: `${data.totalAttendees} / ${data.totalCapacity}`,
                                                                    color: data.utilization > 90 ? '#C62828'
                                                                        : data.utilization > 70 ? '#E8942A'
                                                                            : data.utilization > 50 ? '#F6C445'
                                                                                : '#43A047'
                                                                }))
                                                        )}
                                                    </>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: '100%'
                                                    }}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Training Popularity & Time Preference - Stack on mobile */}
                            <Grid container spacing={isMobile ? 2 : 3} sx={{mb: isMobile ? 2 : 3}}>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        {renderCardHeader(
                                            "Training Type Popularity",
                                            trainingTypesData ? `Period: ${trainingTypesData.periodLabel}` : "Attendance by training type",
                                            "trainingPopularity"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["trainingPopularity"] !== false} timeout="auto">
                                            <CardContent sx={{p: isMobile ? 1.5 : 2}}>
                                                {trainingTypesData ? (() => {
                                                    const rows = [...(trainingTypesData.popularity || [])]
                                                        .sort((a, b) => Number(b.attendees || 0) - Number(a.attendees || 0));
                                                    const total = rows.reduce((sum, row) => sum + Number(row.attendees || 0), 0);
                                                    return renderMetricRows(rows.map((row) => {
                                                        const attendees = Number(row.attendees || 0);
                                                        const share = total > 0 ? Math.round((attendees / total) * 100) : 0;
                                                        return {
                                                            key: row.trainingType || 'Other',
                                                            label: row.trainingType || 'Other',
                                                            value: attendees,
                                                            helper: `${share}%`,
                                                            displayValue: attendees
                                                        };
                                                    }));
                                                })() : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: '100%'
                                                    }}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card>
                                        {renderCardHeader(
                                            "Time of Day",
                                            "Registrations by local time, 24-hour",
                                            "timePreference"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["timePreference"] !== false} timeout="auto">
                                            <CardContent sx={{p: isMobile ? 1.5 : 2}}>
                                                {trainingTypesData ? (() => {
                                                    const rows = trainingTypesData.timePreference || [];
                                                    const total = rows.reduce((sum, row) => sum + Number(row.attendees || 0), 0);
                                                    return renderMetricRows(rows.map((row) => {
                                                        const attendees = Number(row.attendees || 0);
                                                        const share = total > 0 ? Math.round((attendees / total) * 100) : 0;
                                                        return {
                                                            key: row.time_of_day,
                                                            label: row.time_of_day,
                                                            value: attendees,
                                                            helper: `${share}%`,
                                                            displayValue: attendees
                                                        };
                                                    }));
                                                })() : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: '100%'
                                                    }}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Trainer Comparison */}

                            <Grid container spacing={isMobile ? 2 : 3}>
                                <Grid item xs={12}>
                                    <Card>
                                        {renderCardHeader(
                                            "Trainer Performance",
                                            trainerComparisonData ?
                                                `Attendance and return rate, ${trainerComparisonData.periodLabel}`
                                                : "Attendance and return rate",
                                            "trainerPerformance"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["trainerPerformance"] !== false} timeout="auto">
                                            <CardContent sx={{p: isMobile ? 1 : 2}}>
                                                {trainerComparisonData ? (
                                                    <TableContainer sx={{
                                                        maxHeight: isMobile ? 300 : 400,
                                                        overflowX: 'auto'
                                                    }}>
                                                        <Table stickyHeader size={isMobile ? "small" : "medium"}>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Trainer</TableCell>
                                                                    <TableCell align="center">Classes</TableCell>
                                                                    {!isMobile && <TableCell align="center">Total
                                                                        Attendance</TableCell>}
                                                                    <TableCell align="center">Avg Attendance</TableCell>
                                                                    {!isMobile &&
                                                                        <TableCell align="center">Retention</TableCell>}
                                                                    <TableCell align="center">Retention Rate</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {trainerComparisonData.data.map((trainer, index) => {
                                                                    const retentionRate = trainer.totalAttendance > 0 ?
                                                                        (trainer.retention / trainer.totalAttendance * 100).toFixed(1) : '0';

                                                                    return (
                                                                        <TableRow key={index}>
                                                                            <TableCell
                                                                                sx={{whiteSpace: 'nowrap'}}>{trainer.trainerName}</TableCell>
                                                                            <TableCell
                                                                                align="center">{trainer.classesCount}</TableCell>
                                                                            {!isMobile && <TableCell
                                                                                align="center">{trainer.totalAttendance}</TableCell>}
                                                                            <TableCell
                                                                                align="center">{trainer.averageAttendance}</TableCell>
                                                                            {!isMobile && <TableCell
                                                                                align="center">{trainer.retention}</TableCell>}
                                                                            <TableCell align="center">
                                                                                <Box sx={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    flexDirection: isMobile ? 'column' : 'row'
                                                                                }}>
                                                                                    <Typography variant="body2" sx={{
                                                                                        mr: isMobile ? 0 : 1,
                                                                                        mb: isMobile ? 0.5 : 0
                                                                                    }}>
                                                                                        {retentionRate}%
                                                                                    </Typography>
                                                                                    <Box
                                                                                        sx={{width: isMobile ? 40 : 60}}>
                                                                                        <LinearProgress
                                                                                            variant="determinate"
                                                                                            value={Math.min(parseFloat(retentionRate), 100)}
                                                                                            color={
                                                                                                parseFloat(retentionRate) > 70 ? 'success' :
                                                                                                    parseFloat(retentionRate) > 40 ? 'warning' :
                                                                                                        'error'
                                                                                            }
                                                                                            sx={{
                                                                                                height: isMobile ? 6 : 8,
                                                                                                borderRadius: 4
                                                                                            }}
                                                                                        />
                                                                                    </Box>
                                                                                </Box>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: 200
                                                    }}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* Client Retention & Engagement - Mobile Optimized */}
                    {activeTab === 4 && (
                        <Box>




                            {/* Client Achievements - Stack on mobile */}
                            <Grid container spacing={isMobile ? 2 : 3}>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        {renderCardHeader(
                                            "Top Achievers",
                                            clientAchievementsData ? `Period: ${clientAchievementsData.periodLabel}` : "Members with most recorded achievements",
                                            "topAchievers"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["topAchievers"] !== false} timeout="auto">
                                            <CardContent sx={{p: isMobile ? 1.5 : 2}}>
                                                {clientAchievementsData ? (
                                                    clientAchievementsData.topAchievers.length > 0 ? (
                                                        renderMetricRows(clientAchievementsData.topAchievers.map((achiever, index) => ({
                                                            key: index,
                                                            label: `${index + 1}. ${achiever.fullName}`,
                                                            value: achiever.records_count,
                                                            helper: formatDate(achiever.last_record)
                                                        })))
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 3}}>
                                                            No records in the selected period
                                                        </Typography>
                                                    )
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: '100%'
                                                    }}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card>
                                        {renderCardHeader(
                                            "Progress Trends",
                                            "Average improvement in weightlifting exercises",
                                            "progressTrends"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["progressTrends"] !== false} timeout="auto">
                                            <CardContent sx={{p: isMobile ? 1.5 : 2}}>
                                                {clientAchievementsData ? (
                                                    <Box>
                                                        {clientAchievementsData.progressTrends.length > 0 ? (
                                                            renderMetricRows(clientAchievementsData.progressTrends.map((trend) => ({
                                                                key: trend.exercise,
                                                                label: trend.exercise,
                                                                value: Math.abs(Number(trend.avg_improvement) || 0),
                                                                displayValue: `${Number(trend.avg_improvement).toFixed(1)} kg`,
                                                                color: Number(trend.avg_improvement) >= 0 ? '#43A047' : '#C62828'
                                                            })))
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 2}}>
                                                                Not enough data to show progress
                                                            </Typography>
                                                        )}

                                                        <Typography variant="subtitle2" sx={{fontWeight: 700, mt: 2, mb: 0.5}}>
                                                            Records by type
                                                        </Typography>
                                                        {renderMetricRows((clientAchievementsData.recordsByType || []).map((type) => ({
                                                            key: type.type,
                                                            label: type.type,
                                                            value: type.count
                                                        })))}
                                                    </Box>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: '100%'
                                                    }}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>

                            </Grid>
                            <Grid container spacing={isMobile ? 2 : 3} sx={{mt: 0}}>
                                <Grid item xs={12}>
                                    <NewMembersRetention
                                        affiliateId={affiliateId}
                                        affiliateEmail={affiliateEmail}
                                        period={dateRange}
                                    />
                                </Grid>
                            </Grid>
                        </Box>


                    )}

                    {/* Growth Potential & Early Warnings - Mobile Optimized */}
                    {activeTab === 5 && (
                        <Box>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Showing data
                                for: {getPeriodLabel(earlyWarningsData || dormantClientsData || growthOpportunitiesData)}
                            </Typography>

                            {/* Early Warning System */}
                            <Grid container spacing={isMobile ? 2 : 3} sx={{mb: isMobile ? 2 : 3}}>
                                <Grid item xs={12}>
                                    <Card>
                                        {renderCardHeader(
                                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                <Error color="error" sx={{mr: 1}}/>
                                                <Typography variant={isMobile ? "subtitle1" : "h6"}>Early Warning
                                                    System</Typography>
                                                {earlyWarningsData && earlyWarningsData.summary && (
                                                    <Badge
                                                        badgeContent={earlyWarningsData.summary.high}
                                                        color="error"
                                                        sx={{ml: 2}}
                                                    />
                                                )}
                                            </Box>,
                                            "Critical issues requiring attention",
                                            "earlyWarnings"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["earlyWarnings"] !== false} timeout="auto">
                                            <CardContent sx={{p: isMobile ? 1.5 : 2}}>
                                                {earlyWarningsData ? (
                                                    earlyWarningsData.warnings && earlyWarningsData.warnings.length > 0 ? (
                                                        earlyWarningsData.warnings.map((warning, index) => (
                                                            <Box
                                                                key={index}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'flex-start',
                                                                    gap: 1.5,
                                                                    py: 1.15,
                                                                    borderTop: index === 0 ? 'none' : '1px solid',
                                                                    borderColor: 'divider'
                                                                }}
                                                            >
                                                                <Chip
                                                                    label={warning.severity}
                                                                    size="small"
                                                                    color={
                                                                        warning.severity === 'High' ? 'error' :
                                                                            warning.severity === 'Medium' ? 'warning' : 'success'
                                                                    }
                                                                    sx={{mt: 0.25}}
                                                                />
                                                                <Box sx={{flex: 1, minWidth: 0}}>
                                                                    <Typography variant="body2" sx={{fontWeight: 600}}>
                                                                        {warning.type}{warning.user?.fullName ? ` · ${warning.user.fullName}` : ''}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {warning.details}
                                                                    </Typography>
                                                                </Box>
                                                                <Typography variant="caption" color="text.secondary" sx={{flexShrink: 0}}>
                                                                    {formatDate(warning.date)}
                                                                </Typography>
                                                            </Box>
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" color="textSecondary"
                                                                    sx={{textAlign: 'center', py: 3}}>
                                                            No warnings detected - everything looks good!
                                                        </Typography>
                                                    )
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: 200
                                                    }}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Dormant Clients & Growth Opportunities - Stack on mobile */}
                            <Grid container spacing={isMobile ? 2 : 3}>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        {renderCardHeader(
                                            "Dormant Clients",
                                            "Members with active plans but low attendance",
                                            "dormantClients"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["dormantClients"] !== false} timeout="auto">
                                            <CardContent sx={{p: isMobile ? 1.5 : 2}}>
                                                {dormantClientsData ? (
                                                    dormantClientsData.dormantClients && dormantClientsData.dormantClients.length > 0 ? (
                                                        <>
                                                            <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                                                                Active plan holders whose visits dropped by at least half compared with the previous 30 days.
                                                            </Typography>
                                                            {dormantClientsData.dormantClients.map((client, index) => (
                                                                <Box
                                                                    key={index}
                                                                    sx={{
                                                                        py: 1.1,
                                                                        borderTop: index === 0 ? 'none' : '1px solid',
                                                                        borderColor: 'divider'
                                                                    }}
                                                                >
                                                                    <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 1}}>
                                                                        <Typography variant="body2" sx={{fontWeight: 600}}>{client.fullName}</Typography>
                                                                        <Typography variant="caption" color="text.secondary">{formatDate(client.endDate)}</Typography>
                                                                    </Box>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {client.planName} · {client.visits_in_previous_30_days} → {client.visits_in_last_30_days} visits
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                        </>
                                                    ) : (
                                                        <Typography variant="body2" color="textSecondary"
                                                                    sx={{textAlign: 'center', py: 3}}>
                                                            No dormant clients detected
                                                        </Typography>
                                                    )
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: '100%'
                                                    }}>
                                                        <CircularProgress size={30}/>
                                                    </Box>
                                                )}

                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card>
                                        {renderCardHeader(
                                            "Growth Opportunities",
                                            "Members ready for upgrades or renewals",
                                            "growthOpportunities"
                                        )}
                                        <Divider/>
                                        <Collapse in={expandedCards["growthOpportunities"] !== false} timeout="auto">
                                            <CardContent sx={{p: isMobile ? 1.5 : 2}}>
                                                <Typography
                                                    variant={isMobile ? "body2" : "subtitle2"}
                                                    gutterBottom
                                                    sx={{fontWeight: 'medium'}}
                                                >
                                                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                        <TrendingUp sx={{mr: 1, color: 'success.main'}}
                                                                    fontSize="small"/>
                                                        Potential Upgrades
                                                    </Box>
                                                </Typography>
                                                {growthOpportunitiesData ? (
                                                    growthOpportunitiesData.frequentAttenders && growthOpportunitiesData.frequentAttenders.length > 0 ? (
                                                        <Box sx={{mb: 2}}>
                                                            {growthOpportunitiesData.frequentAttenders.map((client, index) => (
                                                                <Box
                                                                    key={index}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                        py: 1,
                                                                        borderTop: index === 0 ? 'none' : '1px solid',
                                                                        borderColor: 'divider'
                                                                    }}
                                                                >
                                                                    <Box sx={{flex: 1, minWidth: 0}}>
                                                                        <Typography variant="body2" noWrap sx={{fontWeight: 600}}>{client.fullName}</Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {client.current_plan} · {client.visits_in_last_30_days} visits / 30 days
                                                                        </Typography>
                                                                    </Box>
                                                                    <Chip
                                                                        label={`${client.sessions_left} left`}
                                                                        size="small"
                                                                        color={client.sessions_left < 5 ? 'error' : 'warning'}
                                                                    />
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" color="textSecondary"
                                                                    sx={{textAlign: 'center', py: 2, mb: 2}}>
                                                            No upgrade opportunities detected
                                                        </Typography>
                                                    )
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: 100
                                                    }}>
                                                        <CircularProgress size={24}/>
                                                    </Box>
                                                )}

                                                <Divider sx={{my: 2}}/>

                                                <Typography
                                                    variant={isMobile ? "body2" : "subtitle2"}
                                                    gutterBottom
                                                    sx={{fontWeight: 'medium'}}
                                                >
                                                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                        <AccessTime sx={{mr: 1, color: 'warning.main'}}
                                                                    fontSize="small"/>
                                                        Active Plans Expiring Soon
                                                    </Box>
                                                </Typography>
                                                {growthOpportunitiesData ? (
                                                    growthOpportunitiesData.expiringActivePlans && growthOpportunitiesData.expiringActivePlans.length > 0 ? (
                                                        growthOpportunitiesData.expiringActivePlans.map((client, index) => (
                                                            <Box
                                                                key={index}
                                                                sx={{
                                                                    py: 1,
                                                                    borderTop: index === 0 ? 'none' : '1px solid',
                                                                    borderColor: 'divider'
                                                                }}
                                                            >
                                                                <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 1}}>
                                                                    <Typography variant="body2" sx={{fontWeight: 600}}>{client.fullName}</Typography>
                                                                    <Typography variant="caption" color="text.secondary">{formatDate(client.endDate)}</Typography>
                                                                </Box>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {client.planName} · {client.visits_in_last_30_days} visits / 30 days
                                                                </Typography>
                                                            </Box>
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" color="textSecondary"
                                                                    sx={{textAlign: 'center', py: 2}}>
                                                            No active plans expiring soon
                                                        </Typography>
                                                    )
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: 100
                                                    }}>
                                                        <CircularProgress size={24}/>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default AnalyticsDashboard;